"use client";

import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import {
  GridV2BrandTilePlaceholder,
  GridV2GenericTilePlaceholder,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/GridV2Placeholders";
import { GridV2ProductTilePlaceholder } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/GridV2Placeholders";
import { SDUIGridV2Tile } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/SDUIGridV2Tile";
import { SDUIGridHeaderRow } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { FilterablePaginatedSectionContext } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";
import {
  DynamicVariable,
  assignInlineVars,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { useSettingEnableVisualNavUpgrade } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableVisualNavUpgrade";
import { getSlateSpacingPixels } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Column } from "@faire/web/slate/Layout";
import { cn } from "@faire/web/tools/cn";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { type JSX, PropsWithChildren, useContext } from "react";

const FALLBACK_NUMBER_OF_TILES_PER_ROW = 2;
const COLLAPSED_COLUMN_VAR_NAME = "--f-fg-columns-collapse-by";

const varMobileNumColumns: DynamicVariable<"--f-sdui-grid-nc-m"> = {
  variable: "--f-sdui-grid-nc-m",
  className: "grid-cols-(--f-sdui-grid-nc-m)",
};

const varTabletNumColumns: DynamicVariable<"--f-sdui-grid-nc-md"> = {
  variable: "--f-sdui-grid-nc-md",
  className: "md:grid-cols-(--f-sdui-grid-nc-md)",
};

const varDesktopNumColumns: DynamicVariable<"--f-sdui-grid-nc-lg"> = {
  variable: "--f-sdui-grid-nc-lg",
  className: "lg:grid-cols-(--f-sdui-grid-nc-lg)",
};

const varXLargeNumColumns: DynamicVariable<"--f-sdui-grid-nc-xl"> = {
  variable: "--f-sdui-grid-nc-xl",
  className: "xl:grid-cols-(--f-sdui-grid-nc-xl)",
};

export const SDUIGridV2FixedColumnLayoutBase: React.FC<{
  gridComponent: IGridComponent;
  loggingData: ComponentLoggingEventParams;
  isRequiredForInteractivity?: boolean;
  renderBehaviour?: SDUIComponentRenderBehaviour;
}> = ({
  gridComponent,
  isRequiredForInteractivity,
  loggingData,
  renderBehaviour,
}) => {
  useTraceComponentRender("SDUIGridV2FixedColumnLayoutBase");

  const {
    column_layout = IGridComponent.IColumnLayout.build(),
    header_background_icon,
    responsive_header_background_icon,
    responsive_title,
    style = IGridStyle.build(),
    tiles,
    title,
    log_tile_batch_view,
  } = gridComponent;

  const {
    num_columns = FALLBACK_NUMBER_OF_TILES_PER_ROW,
    responsive_num_columns,
  } = column_layout.fixed ?? IGridComponent.IFixedColumnLayout.build();
  const {
    column_spacing = Spacing.SPACING_NONE,
    row_spacing = Spacing.SPACING_NONE,
  } = style;

  const columnSpacingPixels = getSlateSpacingPixels(column_spacing);
  const rowSpacingPixels = getSlateSpacingPixels(row_spacing);

  const responsiveNumberOfTilesPerRow =
    convertServerResponsivePropertyToClientResponsiveProperty(
      num_columns,
      (col) => col,
      responsive_num_columns
    );

  const numTilesPerRow = useResponsiveValue(responsiveNumberOfTilesPerRow);
  const {
    data: { isSideBarVisible },
  } = useFilterStore(["isSideBarVisible"]);
  const isSideBarCollapsible = useSettingEnableVisualNavUpgrade();

  const isCollapsibleSideBarVisible =
    isSideBarVisible &&
    isSideBarCollapsible &&
    loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE &&
    loggingData.section_type === "FilterablePaginatedSection";

  const mobileNumberOfColumns = responsiveNumberOfTilesPerRow.mobileAndAbove;
  const tabletNumberOfColumns =
    responsiveNumberOfTilesPerRow.tabletAndAbove ?? mobileNumberOfColumns;
  const desktopNumberOfColumns =
    responsiveNumberOfTilesPerRow.desktopAndAbove ?? tabletNumberOfColumns;
  const xLargeNumberOfColumns =
    responsiveNumberOfTilesPerRow.xLargeAndAbove ?? desktopNumberOfColumns;

  return (
    <Column
      className="w-full"
      style={assignInlineVars({
        [COLLAPSED_COLUMN_VAR_NAME]: `${isCollapsibleSideBarVisible ? 1 : 0}`,
      })}
    >
      <SDUIGridHeaderRow
        title={title}
        responsiveTitle={responsive_title}
        backgroundIcon={header_background_icon}
        responsiveBackgroundIcon={responsive_header_background_icon}
        loggingData={loggingData}
      />
      <div
        className={cn(
          "grid justify-between overflow-hidden",
          varMobileNumColumns.className,
          varTabletNumColumns.className,
          varDesktopNumColumns.className,
          varXLargeNumColumns.className
        )}
        style={{
          columnGap: `${columnSpacingPixels}px`,
          marginTop: `calc(-0.5*${rowSpacingPixels}px)`,
          marginBottom: `calc(-0.5*${rowSpacingPixels}px)`,
          ...assignInlineVars({
            [varMobileNumColumns.variable]: `repeat(${mobileNumberOfColumns}, minmax(0, 1fr))`,
            [varTabletNumColumns.variable]: `repeat(${tabletNumberOfColumns}, minmax(0, 1fr))`,
            [varDesktopNumColumns.variable]: `repeat(calc(${desktopNumberOfColumns} - var(--f-fg-columns-collapse-by)), minmax(0, 1fr))`,
            [varXLargeNumColumns.variable]: `repeat(calc(${xLargeNumberOfColumns} - var(--f-fg-columns-collapse-by)), minmax(0, 1fr))`,
          }),
        }}
      >
        {tiles.map((tile, index) => {
          // first row should be visible for UPLT calculations
          const shouldForceVisibility =
            index < numTilesPerRow && isRequiredForInteractivity;
          const adjacentTileTokens = findAdjacentTileTokens(
            tiles,
            index,
            numTilesPerRow
          );
          return (
            <MaybeWhenVisibilityIsImminent
              key={getTileKey(tile, index)}
              forceVisibility={shouldForceVisibility}
              placeholder={getRowPlaceholder(tile)}
              renderBehaviour={renderBehaviour}
            >
              <SDUIGridV2Tile
                tile={tile}
                adjacentTileTokens={adjacentTileTokens}
                loggingData={loggingData}
                logTileBatchView={
                  adjacentTileTokens ? log_tile_batch_view : undefined
                }
                rowSpacingPixels={rowSpacingPixels}
                shouldSetIsScrollingTopToFalseOnView={
                  index === 0 &&
                  isRequiredForInteractivity &&
                  renderBehaviour?.gridOptimizations
                }
              />
            </MaybeWhenVisibilityIsImminent>
          );
        })}
      </div>
    </Column>
  );
};

const findAdjacentTileTokens = (
  tiles: Array<ITile>,
  index: number,
  numTilesPerRow: number
): string[] | undefined => {
  if (index % numTilesPerRow !== 0) {
    return undefined;
  }

  const adjacentTileTokens = tiles
    .slice(index, index + numTilesPerRow)
    .map((tile) => tile.logging_data?.instance_identifier)
    .filter(isNotUndefined);

  return adjacentTileTokens.length > 0 ? adjacentTileTokens : undefined;
};

const getTileKey = (tile: ITile, index: number) => {
  return tile.logging_data
    ? `${tile.logging_data?.instance_identifier}-${tile.logging_data?.tile_position}`
    : index;
};

const getRowPlaceholder = (tile?: ITile) => {
  if (!tile) {
    return undefined;
  }

  if (tile.brand_tile || tile.brand_tile_with_action) {
    return <GridV2BrandTilePlaceholder />;
  } else if (tile.product_tile || tile.product_with_brand_info_tile) {
    return <GridV2ProductTilePlaceholder />;
  }

  return <GridV2GenericTilePlaceholder />;
};

const MaybeWhenVisibilityIsImminent: React.FC<
  PropsWithChildren & {
    forceVisibility?: boolean;
    placeholder?: JSX.Element;
    renderBehaviour?: SDUIComponentRenderBehaviour;
  }
> = ({ forceVisibility, placeholder, children, renderBehaviour }) => {
  const { isScrollingToTop } = useContext(FilterablePaginatedSectionContext);
  if (
    !forceVisibility &&
    isScrollingToTop &&
    renderBehaviour?.gridOptimizations
  ) {
    if (placeholder) {
      return placeholder;
    }
  }
  if (!forceVisibility && placeholder) {
    return (
      <WhenVisibilityIsImminent customizePlaceholder={placeholder}>
        {children}
      </WhenVisibilityIsImminent>
    );
  }

  return children;
};
